import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import { autoShelfBook } from '../../hooks/useAutoShelves';
import { notifyBookAdded } from './LibraryDock';
import { getPurchaseLinks } from '../../hooks/useLibrary';
import { fetchAllRowsResult } from '../../lib/fetchAll';
import { BookDetailModal } from './BookDetailModal';

import { onCoverError } from '../../lib/coverFallback';
import { similarityFromDistance, clearsThreshold } from '../../lib/matchThreshold';
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// Ticket 23: shape a match_books row into a card. match_books itself returns
// no trope/tone data -- that was trope-preference matching, a separate
// mechanism retired this same ticket, and it never runs against a craving.
// tropes and moods ARE filled in by the caller, which fetches book_tags
// separately for these ids and passes the result here as bookTagsById:
// addBookToLibrary's auto-shelving and the trope/mood/spice chips all read
// them off the card.
//
// matchPercent WAS always null here (match_books returned no percentage at
// all before ticket 27). Ticket 27 bullet 1: "the starter's '% MATCH' badge
// on the card is this ticket's too: it states how close a book is, so it
// goes up with the threshold, not before it." match_books' own `distance`
// column (raw cosine distance, unfiltered -- this deck is called WITHOUT a
// similarity_threshold on purpose, see ReaderHomepage.jsx's runCravingMatch)
// is converted through the one shared SIMILARITY_THRESHOLD vocabulary
// (src/lib/matchThreshold.js), never a second, locally-invented percentage.
// clearsMatch decides the badge's wording ("X% Match" vs "Closest: X%"),
// never whether the book renders at all -- ticket 27 shows the closest
// results labelled as closest, it never hides them.
function mapCravingBooks(rows, bookTagsById = {}) {
  return (rows || []).map(r => {
    const tags = bookTagsById[r.book_id] || { tropes: [], moods: [], spice: [] };
    const similarity = similarityFromDistance(r.distance);
    return {
      id: r.book_id, title: r.title, author: r.author_name,
      coverUrl: r.cover_image_url, description: r.description, avgRating: r.avg_rating,
      tropes: tags.tropes, moods: tags.moods, spice: tags.spice,
      similarity,
      matchPercent: similarity === null ? null : Math.round(similarity * 100),
      clearsMatch: clearsThreshold(similarity),
    };
  });
}

const REPORT_REASONS = [
  'Wrong book information',
  'Incorrect or missing cover',
  'Inappropriate content',
  'Duplicate book',
  'Spam or fake entry',
  'Other',
];

// Ticket 23: this deck now only ever shows the result of a craving search --
// there is no default "browse everything" fetch to fall back to, and no
// in-card refresh, because the page never renders this component until
// matchedBooks is a real array (see ReaderHomepage.jsx). That is what makes
// the old race between a self-fetch and a craving result impossible: there
// is only ever one source of books now.
export default function MatchMeCard({ matchedBooks = null, matchedBooksLoading = false, onSwipe, onAuthRequired, onNewCraving, cravingKey, hasMore = false, loadingMore = false, onLoadMore, loadMoreError = false, onDeckAction }) {
  const { user } = useAuth();
  const touchStartX = useRef(null);
  const isMobile = useIsMobile();

  const [books, setBooks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const lastMatchedBooksRef = useRef(undefined); // which matchedBooks array/null we've already applied
  const lastCravingKeyRef   = useRef(undefined); // ticket 22: which craving/filter-run that array belonged to
  const [isAnimating, setIsAnimating] = useState(false);
  const [swipeDir, setSwipeDir] = useState(null);
  const [userLibrary, setUserLibrary] = useState([]);
  const [buyOpen, setBuyOpen] = useState(false);
  // null while a fetch is in flight, an array once it has come back. The
  // distinction matters: [] means "this book genuinely has none", and only
  // that should show the empty message.
  const [purchaseLinks, setPurchaseLinks] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [detailBookId, setDetailBookId] = useState(null);
  // Distinguishes "the book_tags read failed" from "these books genuinely
  // have no tags" -- see applyCravingMatches below. Same class of bug as
  // finding 2 (error vs. empty conflation) on the matcher itself.
  const [tagFetchError, setTagFetchError] = useState(false);

  // match_books doesn't return trope/tone/spice data, but auto-shelving and
  // the trope/mood/spice chips all need it -- one extra paged read, keyed
  // back onto each book's id. fetchAllRowsResult (not fetchAllRows) so a
  // failed read is { ok: false }, not indistinguishable from zero real tags
  // (finding 5, PR 231 review): the caller decides what "no tags" means
  // instead of the read silently deciding it for them.
  const applyCravingMatches = useCallback(async (booksToMatch, isCurrent = () => true) => {
    setTagFetchError(false);
    const bookTagsById = {};
    if (booksToMatch.length > 0) {
      const ids = booksToMatch.map(r => r.book_id);
      const { ok, rows, error } = await fetchAllRowsResult(() => supabase
        .from('book_tags')
        .select('book_id, tag_type, tropes (canonical_name)')
        .in('book_id', ids)
        .in('tag_type', ['trope', 'tone', 'spice']));
      if (!isCurrent()) return; // a newer craving landed while this was in flight
      if (!ok) {
        console.error('book_tags fetch failed:', error);
        setTagFetchError(true);
        return;
      }
      rows.forEach(bt => {
        const name = bt.tropes?.canonical_name;
        if (!name) return;
        if (!bookTagsById[bt.book_id]) bookTagsById[bt.book_id] = { tropes: [], moods: [], spice: [] };
        const bucket = bt.tag_type === 'tone' ? 'moods' : bt.tag_type === 'spice' ? 'spice' : 'tropes';
        bookTagsById[bt.book_id][bucket].push(name);
      });
    }
    if (!isCurrent()) return;
    setBooks(mapCravingBooks(booksToMatch, bookTagsById));
  }, []);

  // Ticket 22 (remainder): "show me more" for the SAME craving. Unlike
  // applyCravingMatches, this never touches currentIndex or the cards
  // already on screen -- it only fetches tags for the newly-arrived rows
  // and appends them, so a reader mid-deck keeps their place when more
  // cards land behind the one they're looking at.
  const appendCravingMatches = useCallback(async (newRows, isCurrent = () => true) => {
    const bookTagsById = {};
    if (newRows.length > 0) {
      const ids = newRows.map(r => r.book_id);
      const { ok, rows, error } = await fetchAllRowsResult(() => supabase
        .from('book_tags')
        .select('book_id, tag_type, tropes (canonical_name)')
        .in('book_id', ids)
        .in('tag_type', ['trope', 'tone', 'spice']));
      if (!isCurrent()) return;
      if (!ok) {
        // Unlike the initial load, a failed tag read here must not blank
        // out cards the reader is already looking at with tagFetchError --
        // append the new ones untagged rather than losing the whole deck
        // over a read that only affects the books arriving behind it.
        console.error('book_tags fetch failed (load more):', error);
        setBooks(prev => [...prev, ...mapCravingBooks(newRows, {})]);
        return;
      }
      rows.forEach(bt => {
        const name = bt.tropes?.canonical_name;
        if (!name) return;
        if (!bookTagsById[bt.book_id]) bookTagsById[bt.book_id] = { tropes: [], moods: [], spice: [] };
        const bucket = bt.tag_type === 'tone' ? 'moods' : bt.tag_type === 'spice' ? 'spice' : 'tropes';
        bookTagsById[bt.book_id][bucket].push(name);
      });
    }
    if (!isCurrent()) return;
    setBooks(prev => [...prev, ...mapCravingBooks(newRows, bookTagsById)]);
  }, []);

  // The reader's library, for the Saved badge and disabling an already-saved
  // Save button. Independent of which craving is showing.
  useEffect(() => {
    if (!user) { setUserLibrary([]); return; }
    let current = true;
    supabase.from('user_library').select('book_id, csv_book_id, shelf').eq('user_id', user.id)
      .then(({ data }) => { if (current) setUserLibrary(data || []); })
      .catch(err => console.error('Failed to load library:', err.message));
    return () => { current = false; };
  }, [user]);

  useEffect(() => {
    if (matchedBooksLoading) return;
    if (lastMatchedBooksRef.current === matchedBooks) return; // already applied this exact array/null
    const prevBooks = lastMatchedBooksRef.current;
    // Ticket 22 (remainder): a new cravingKey means a genuinely different
    // search (a new craving, a retry, a filter just applied) -- reset to
    // card one, same as always. An UNCHANGED cravingKey with a LONGER array
    // means the parent's "show me more" landed: keep the reader's place and
    // just extend the deck. A shorter or equal-length array under the same
    // key would not be a load-more by construction, so it falls back to the
    // safe default (a full reset) rather than guessing.
    const isLoadMore = lastCravingKeyRef.current === cravingKey && !!prevBooks && !!matchedBooks
      && matchedBooks.length > prevBooks.length;
    lastMatchedBooksRef.current = matchedBooks;
    lastCravingKeyRef.current = cravingKey;

    if (!matchedBooks) { setCurrentIndex(0); setBooks([]); return; }

    if (isLoadMore) {
      const newRows = matchedBooks.slice(prevBooks.length);
      if (newRows.length === 0) return;
      let current = true;
      appendCravingMatches(newRows, () => current);
      return () => { current = false; };
    }

    setCurrentIndex(0);
    setShowDescription(false);
    setTagFetchError(false);
    let current = true;
    applyCravingMatches(matchedBooks, () => current);
    return () => { current = false; };
  }, [matchedBooks, matchedBooksLoading, cravingKey, applyCravingMatches, appendCravingMatches]);

  useEffect(() => {
    const book = books[currentIndex];
    if (!book?.id) return;
    // Clear first. Without this the previous book's links stay in state until
    // the new fetch lands, so a quick swipe then Buy would list the last
    // book's retailers under this one.
    setPurchaseLinks(null);
    setBuyOpen(false);
    // Ignore a response that arrives after the reader has already moved on —
    // swiping fast can otherwise let an older fetch resolve last and win.
    let current = true;
    getPurchaseLinks(book.id)
      .then(links => { if (current) setPurchaseLinks(links || []); })
      .catch(err => { if (current) console.error('getPurchaseLinks failed:', err.message); });
    return () => { current = false; };
  }, [books, currentIndex]);

  async function submitReport() {
    if (!reportReason) return;
    const book = books[currentIndex];
    if (!book?.id) return;
    setReportSubmitting(true);
    try {
      const user = await getCurrentUser();
      await supabase.from('reports').insert({
        entity_type:      'book',
        entity_id:        String(book.id),
        reporter_id:      user?.id || null,
        reason:           reportReason,
        status:           'open',
      });
      // Also flag on the book itself
      await supabase.from('books').update({ flag_reason: reportReason }).eq('id', book.id);
      setReportSuccess(true);
      setTimeout(() => {
        setReportOpen(false);
        setReportReason('');
        setReportSuccess(false);
        advance();
      }, 1600);
    } catch (err) {
      console.error('Report error:', err);
      alert('Failed to submit report: ' + err.message);
    } finally {
      setReportSubmitting(false);
    }
  }

  // Advancing past the last card means the deck exhausted -- the render
  // below catches currentIndex >= books.length and shows that end state,
  // which (ticket 22) now offers "show me more" when onLoadMore says there
  // is another page, rather than always ending the craving outright.
  const advance = () => {
    if (isAnimating) return;
    onSwipe?.();
    setIsAnimating(true);
    setBuyOpen(false);
    setTimeout(() => {
      setCurrentIndex(prev => Math.min(prev + 1, books.length));
      setIsAnimating(false);
      setSwipeDir(null);
    }, 300);
  };

  const goBack = () => {
    if (isAnimating) return;
    setBuyOpen(false);
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const handleSkip = async () => {
    const book = books[currentIndex];
    if (!book?.id) return;
    setSwipeDir('left');
    if (user) await supabase.from('user_book_actions').upsert({ user_id: user.id, book_id: parseInt(book.id), action: 'skip' });
    setTimeout(() => advance(), 150);
  };

  async function addBookToLibrary(book, shelf, forUser, { notify = true } = {}) {
    const { data: newEntry, error } = await supabase.from('user_library').insert({
      user_id: forUser.id, book_id: parseInt(book.id), csv_book_id: null,
      shelf, date_added: new Date().toISOString(),
    }).select('id').single();
    if (error) { console.error('Failed to add book:', error); return false; }
    const tropes = (book.tropes || []).filter(Boolean);
    if (tropes.length > 0 && newEntry?.id) {
      autoShelfBook(newEntry.id, { genres: [], tropes }, forUser.id).catch(err => console.warn('Auto-shelf error:', err.message));
    }
    if (notify) notifyBookAdded({ id: book.id, title: book.title, author: book.author, coverUrl: book.coverUrl, color: '#9E4B2C', shelf });
    return true;
  }

  // Somebi's PR #254 review, finding #1: `user` here is whatever useAuth()
  // handed this render -- closed over the moment handleSave/handleAlreadyRead
  // was created. onAuthRequired's retry is that exact same closure, and
  // useGuestGate's auth listener runs it synchronously, before React has
  // re-rendered this component with the now-signed-in user -- so the retry's
  // `user` is still null, it calls onAuthRequired again, and the gate reopens
  // forever instead of saving. getCurrentUser() doesn't have that problem: its
  // module-level cache is updated at the top of the single onAuthStateChange
  // handler (lib/auth.js), before any subscriber -- including this gate's --
  // runs, so it already has the fresh user by the time this retry asks.
  const handleSave = async () => {
    const forUser = user || await getCurrentUser();
    if (!forUser) { onAuthRequired?.(handleSave); return; }
    const book = books[currentIndex];
    if (!book?.id) return;
    setSwipeDir('right');
    const success = await addBookToLibrary(book, 'tbr', forUser, { notify: true });
    if (!success) { alert('Could not save that book. Please try again.'); return; }
    // The user_library row addBookToLibrary just wrote IS the record of a save.
    // Do not also write user_book_actions here: its CHECK constraint allows only
    // skip / view / click, so the old value was rejected on every call and never
    // landed. That table stays for skips, views and clicks only.
    setUserLibrary(prev => [...prev, { book_id: book.id, shelf: 'tbr' }]);
    // Ticket 80: tie this save to the craving that showed the book. After the
    // save, never before it, and never awaited: the save has already landed.
    onDeckAction?.(book, 'save');
    setTimeout(() => advance(), 150);
  };

  const handleAlreadyRead = async () => {
    const forUser = user || await getCurrentUser();
    if (!forUser) { onAuthRequired?.(handleAlreadyRead); return; }
    const book = books[currentIndex];
    if (!book?.id) return;
    const success = await addBookToLibrary(book, 'read', forUser, { notify: true });
    if (success) {
      // Same as handleSave: the shelf = 'read' row is the record. The old action
      // value is not one the constraint allows, so it never wrote.
      setUserLibrary(prev => [...prev, { book_id: book.id, shelf: 'read' }]);
      onDeckAction?.(book, 'read'); // ticket 80, as in handleSave
    }
    advance();
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (matchedBooksLoading) {
    return (
      <div style={{ background: 'var(--brand-white)', borderRadius: 26, padding: isMobile ? '40px 20px' : '60px 40px', textAlign: 'center', border: '3px solid var(--neutral-800)', boxShadow: '8px 8px 0 rgba(0,0,0,0.06)' }}>
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
        <p style={{ color: 'var(--neutral-500)', fontSize: 'var(--font-size-md)' }}>Finding your matches...</p>
      </div>
    );
  }

  if (!matchedBooks) return null; // no craving run yet -- ReaderHomepage doesn't render this section at all in that case

  if (tagFetchError) {
    return (
      <div style={{ background: 'var(--brand-white)', border: '3px solid var(--neutral-800)', borderRadius: 26, padding: isMobile ? '40px 20px' : '60px 40px', textAlign: 'center', boxShadow: '8px 8px 0 rgba(0,0,0,0.06)' }}>
        <p style={{ color: 'var(--neutral-800)', fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-6)' }}>Couldn't reach the matcher</p>
        <p style={{ color: 'var(--neutral-500)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-14)' }}>
          That's on us, not your craving, try again.
        </p>
        <button
          onClick={() => applyCravingMatches(matchedBooks)}
          style={{ background: 'var(--brand-gold)', color: 'var(--neutral-800)', border: 'none', borderRadius: 10, padding: '10px 24px', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (books.length === 0) {
    return (
      <div style={{ background: 'var(--brand-white)', borderRadius: 26, padding: isMobile ? '40px 20px' : '60px 40px', textAlign: 'center', border: '3px solid var(--neutral-800)', boxShadow: '8px 8px 0 rgba(0,0,0,0.06)' }}>
        <p style={{ color: 'var(--neutral-800)', fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-6)' }}>Nothing matched right now</p>
        <p style={{ color: 'var(--neutral-500)', fontSize: 'var(--font-size-sm)' }}>
          Nothing matched that closely yet, try describing it a different way.
        </p>
      </div>
    );
  }

  // ── Deck exhausted: swiped or saved through every match for this craving ──
  // Ticket 22 (remainder): hasMore/onLoadMore turn this from a dead end into
  // a real "next page" -- the craving isn't actually over, match_books just
  // hasn't been asked for more of it yet.
  if (currentIndex >= books.length) {
    return (
      <div style={{ background: 'var(--brand-white)', border: '3px solid var(--neutral-800)', borderRadius: 26, padding: isMobile ? '34px 24px' : '48px 40px', textAlign: 'center', boxShadow: '8px 8px 0 rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 19 : 23, fontWeight: 700, color: 'var(--neutral-800)', margin: 0 }}>
          {hasMore ? "That's everything so far." : "That's the deck for this craving."}
        </h3>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '10px 0 0' }}>
          {hasMore
            ? 'There are more books that matched -- or chase a different feeling entirely.'
            : 'Every card was matched to your words. Want to chase a different feeling?'}
        </p>
        {hasMore && loadMoreError && (
          <p style={{ color: 'var(--brand-rust)', fontSize: 'var(--font-size-xs)', margin: '14px 0 0' }}>Couldn't load more books, try again.</p>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'center', marginTop: 18, flexWrap: 'wrap' }}>
          {hasMore && (
            <button onClick={onLoadMore} disabled={loadingMore} style={{ background: 'var(--brand-gold)', color: 'var(--neutral-800)', border: 'none', borderRadius: 14, padding: '13px 28px', fontSize: 'var(--font-size-sm)', fontWeight: 800, cursor: loadingMore ? 'default' : 'pointer', opacity: loadingMore ? 0.7 : 1 }}>
              {loadingMore ? 'finding more…' : 'show me more'}
            </button>
          )}
          <button onClick={onNewCraving} style={{ background: 'var(--brand-forest)', color: 'var(--brand-white)', border: 'none', borderRadius: 14, padding: '13px 28px', fontSize: 'var(--font-size-sm)', fontWeight: 800, cursor: 'pointer', boxShadow: '4px 5px 0 rgba(45,74,62,0.25)' }}>
            start a new craving
          </button>
        </div>
      </div>
    );
  }

  const book = books[currentIndex];

  const isInLibrary = userLibrary.some(item => item.book_id === book.id);
  const isInTBR     = userLibrary.some(item => item.book_id === book.id && item.shelf === 'tbr');

  const spiceLevel = book.spice?.find(t => ['No Spice', 'Mild', 'Moderate', 'Spicy', 'Explicit'].some(s => t.includes(s))) || null;
  const spiceCount = spiceLevel === null ? null
    : spiceLevel.includes('Explicit') ? 5
    : spiceLevel.includes('Spicy')    ? 4
    : spiceLevel.includes('Moderate') ? 3
    : spiceLevel.includes('Mild')     ? 2
    : spiceLevel.includes('No Spice') ? 0
    : null; // an unrecognised spice tag -- don't invent a rating for it either
  const displayTropes = book.tropes?.slice(0, isMobile ? 3 : 4) || [];
  const displayMoods  = (book.moods || []).slice(0, isMobile ? 2 : 3);

  return (
    <>
    <div style={{ marginBottom: 'var(--space-xxl)' }}>
      {/* Stack shadow */}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: 8, left: 6, right: 6, bottom: -4, background: 'rgba(41,37,36,0.06)', borderRadius: 26, transform: 'rotate(1.5deg)', border: '1.5px solid rgba(41,37,36,0.04)' }} />

        {/* Swipe overlays */}
        {swipeDir === 'left' && (
          <div style={{ position: 'absolute', inset: 0, borderRadius: 26, background: 'rgba(120,113,108,0.06)', border: '3px solid rgba(120,113,108,0.35)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <span style={{ fontSize: 36, fontWeight: 900, color: 'var(--neutral-500)', transform: 'rotate(-12deg)', opacity: 0.75 }}>NOPE</span>
          </div>
        )}
        {swipeDir === 'right' && (
          <div style={{ position: 'absolute', inset: 0, borderRadius: 26, background: 'rgba(5,150,105,0.06)', border: '3px solid rgba(5,150,105,0.35)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <span style={{ fontSize: 36, fontWeight: 900, color: 'var(--feedback-success)', transform: 'rotate(12deg)', opacity: 0.75 }}>YES!</span>
          </div>
        )}

        <div
          style={{
            borderRadius: 26, overflow: 'hidden',
            background: 'var(--brand-white)',
            border: '3px solid var(--neutral-800)',
            boxShadow: '8px 8px 0 rgba(0,0,0,0.06)',
            position: 'relative', zIndex: 5,
            transform: swipeDir === 'right' ? 'translateX(120%) rotate(12deg)' : swipeDir === 'left' ? 'translateX(-120%) rotate(-12deg)' : 'none',
            transition: swipeDir ? 'transform 0.3s ease' : 'none',
          }}
          onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            if (touchStartX.current === null) return;
            const delta = e.changedTouches[0].clientX - touchStartX.current;
            touchStartX.current = null;
            if (Math.abs(delta) < 40) return;
            if (delta < 0) handleSkip();
            else handleSave();
          }}
        >
          {/* Nav arrows — desktop only */}
          {!isMobile && (
            <>
              <button onClick={goBack} style={{ position: 'absolute', left: 12, top: '40%', transform: 'translateY(-50%)', width: 32, height: 32, borderRadius: 'var(--radius-circle)', background: 'rgba(41,37,36,0.05)', border: '1px solid rgba(41,37,36,0.1)', cursor: 'pointer', fontSize: 'var(--font-size-md)', color: 'var(--neutral-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>←</button>
              <button onClick={advance} style={{ position: 'absolute', right: 12, top: '40%', transform: 'translateY(-50%)', width: 32, height: 32, borderRadius: 'var(--radius-circle)', background: 'rgba(41,37,36,0.05)', border: '1px solid rgba(41,37,36,0.1)', cursor: 'pointer', fontSize: 'var(--font-size-md)', color: 'var(--neutral-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>→</button>
            </>
          )}

          {/* Main content */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: isMobile ? 'auto' : 300 }}>

            {/* Cover — tap for the full detail popup */}
            <div
              onClick={() => setDetailBookId(book.id)}
              title="tap for full details"
              style={{
                width: isMobile ? '100%' : 220,
                height: isMobile ? 160 : 'auto',
                flexShrink: 0,
                position: 'relative',
                overflow: 'hidden',
                background: 'var(--brand-cream)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}>
              {book.coverUrl
                ? <img src={book.coverUrl} alt={book.title} style={{ width: isMobile ? 'auto' : '100%', height: isMobile ? '100%' : '100%', maxWidth: '100%', objectFit: isMobile ? 'contain' : 'cover', display: 'block' }} onError={onCoverError} />
                : <div style={{ width: '100%', height: '100%', minHeight: isMobile ? 160 : 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 64, opacity: 0.3 }}>📚</span>
                  </div>
              }
              {/* Saved badge */}
              {isInTBR && (
                <div style={{ position: 'absolute', top: 10, left: 10, background: 'var(--brand-gold)', color: 'var(--brand-white)', padding: '4px 10px', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xs)', fontWeight: 700 }}>♥ Saved</div>
              )}
              {/* Match badge (ticket 27 bullet 1). Opposite corner from Saved
                  so the two never collide -- Saved is reader-driven and left,
                  this one is the system's own read on the book and sits
                  right. Wording is the whole point: "X% Match" only once the
                  similarity actually clears SIMILARITY_THRESHOLD, "Closest:
                  X%" below it -- never hidden, never silently promoted. */}
              {book.matchPercent !== null && (
                <div style={{ position: 'absolute', top: 10, right: 10, background: book.clearsMatch ? 'var(--brand-forest)' : 'var(--neutral-500)', color: 'var(--brand-white)', padding: '4px 10px', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xs)', fontWeight: 700 }}>
                  {book.clearsMatch ? `${book.matchPercent}% Match` : `Closest: ${book.matchPercent}%`}
                </div>
              )}
            </div>

            {/* Details */}
            <div style={{ flex: 1, padding: isMobile ? '18px 18px 4px' : '24px 28px 20px', display: 'flex', flexDirection: 'column', gap: 'var(--space-none)' }}>
              {/* Spice row -- omitted entirely when this book has no spice tag,
                  rather than showing an all-faded row that still reads as "0". */}
              {spiceCount !== null && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 'var(--space-10)' }}>
                  <div style={{ display: 'flex', gap: 'var(--space-xxs)' }}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} style={{ fontSize: 'var(--font-size-xs)', opacity: i < spiceCount ? 1 : 0.15 }}>🌶️</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Title + author */}
              <h2 onClick={() => setDetailBookId(book.id)} style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 26, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px', lineHeight: 1.1, cursor: 'pointer' }}>{book.title}</h2>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 14px' }}>{book.author}</p>

              {/* Trope + mood tags */}
              {(displayTropes.length > 0 || displayMoods.length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', marginBottom: 'var(--space-14)' }}>
                  {displayTropes.map((trope, i) => (
                    <span key={`t${i}`} style={{ fontSize: 'var(--font-size-xs)', padding: '5px 13px', borderRadius: 14, background: 'var(--brand-cream)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' }}>{trope}</span>
                  ))}
                  {displayMoods.map((mood, i) => (
                    <span key={`m${i}`} style={{ fontSize: 'var(--font-size-xs)', padding: '5px 13px', borderRadius: 14, background: 'rgba(212,168,75,0.12)', color: 'var(--brand-gold)', border: '1px solid rgba(212,168,75,0.35)' }}>{mood}</span>
                  ))}
                </div>
              )}

              {/* Description — collapsed to two lines until the reader asks for
                  more. This is the only prose on the card saying what the book
                  is, and it should be there for a swipe decision, but a long
                  author description no longer forces the card's height. */}
              {book.description && (
                <>
                  <p style={{
                    fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.6, margin: '0 0 6px',
                    ...(showDescription
                      ? {}
                      : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                  }}>
                    {book.description}
                  </p>
                  <button onClick={() => setShowDescription(s => !s)} style={{ background: 'none', border: 'none', color: 'var(--brand-rust)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 'var(--space-14)', alignSelf: 'flex-start' }}>
                    {showDescription ? 'hide their description ▲' : 'read their description ▼'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Button row */}
          <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: 'center', gap: isMobile ? 8 : 10, padding: isMobile ? '20px 14px 22px' : '12px 24px 22px', borderTop: '1px solid var(--brand-sand)' }}>

            {/* Skip */}
            <button
              onClick={handleSkip}
              disabled={isAnimating}
              style={{ flex: isMobile ? '1 1 45%' : 1, padding: isMobile ? '12px 8px' : '12px 18px', borderRadius: 14, background: 'var(--brand-cream)', border: '1.5px solid var(--brand-sand)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
            >
              <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>✕</span>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', fontWeight: 600 }}>Skip</span>
            </button>

            {/* Save */}
            <button
              onClick={handleSave}
              disabled={isAnimating || isInLibrary}
              style={{ flex: isMobile ? '1 1 45%' : 1.6, padding: isMobile ? '12px 8px' : '12px 20px', borderRadius: 14, background: isInLibrary ? 'var(--brand-cream)' : 'var(--brand-rust)', border: `1.5px solid ${isInLibrary ? 'var(--brand-sand)' : 'var(--brand-rust)'}`, cursor: isInLibrary ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-6)' }}
            >
              <span style={{ fontSize: 15, color: isInLibrary ? 'var(--neutral-400)' : 'var(--brand-white)' }}>♥</span>
              <span style={{ fontSize: 'var(--font-size-sm)', color: isInLibrary ? 'var(--neutral-400)' : 'var(--brand-white)', fontWeight: 700 }}>
                {isInLibrary ? 'Saved' : 'Save'}
              </span>
            </button>

            {/* Buy */}
            <div style={{ position: 'relative', flex: isMobile ? '1 1 45%' : 1 }}>
              <button
                onClick={() => { if (!user) { onAuthRequired?.(() => setBuyOpen(p => !p)); return; } setBuyOpen(p => !p); }}
                style={{ width: '100%', padding: isMobile ? '12px 8px' : '12px 18px', borderRadius: 14, background: 'rgba(212,168,75,0.14)', border: '1.5px solid rgba(212,168,75,0.4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-xs)' }}
              >
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--brand-gold)', fontWeight: 600 }}>Buy</span>
                <span style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--brand-gold)' }}>▾</span>
              </button>
              {buyOpen && (
                <div style={{ position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)', background: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', padding: '6px 0', border: '1px solid var(--brand-sand)', minWidth: 130, zIndex: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
                  {(purchaseLinks || []).map((link, i) => (
                    <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                      // Ticket 80: Buy is the retailer tap, not opening the list.
                      // Opening it records nothing; the tap that leaves for a shop does.
                      onClick={() => onDeckAction?.(book, 'buy')}
                      style={{ display: 'block', padding: '8px 14px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', textDecoration: 'none' }}>
                      {link.retailer} ↗
                    </a>
                  ))}
                  {/* No invented search link here. If nothing real is stored
                      for this book, say so rather than send the reader off to
                      hunt for it themselves. Only once the fetch has returned
                      though — null still means "loading", and claiming there
                      are none before we know is its own kind of wrong. */}
                  {purchaseLinks && purchaseLinks.length === 0 && (
                    <p style={{ margin: 'var(--space-none)', padding: '8px 14px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>
                      No buy links yet
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Already Read -- gold-tinted per CLAUDE.md, never greyed/disabled-looking */}
            <button
              onClick={handleAlreadyRead}
              disabled={isAnimating}
              style={{ flex: isMobile ? '1 1 45%' : 1.6, padding: isMobile ? '12px 8px' : '12px 18px', borderRadius: 14, background: 'rgba(212,168,75,0.12)', border: '1.5px solid rgba(212,168,75,0.35)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--brand-gold)', fontWeight: 600, whiteSpace: 'nowrap' }}>Already Read</span>
            </button>

            {/* Report flag */}
            <button onClick={() => setReportOpen(true)} title="Report this book"
              style={{ width: 44, height: 44, borderRadius: 14, border: '1.5px solid var(--brand-sand)', background: 'var(--brand-cream)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 'var(--font-size-lg)' }}>
              🚩
            </button>
          </div>

          {/* Dot indicators. Pre-existing cap of 7 visible dots -- fixed
              to always show books[0..6] regardless of deck size, so once a
              reader paged past card 7 none of them ever matched currentIndex
              and the active dot just vanished. Now the 7-dot window slides
              to keep currentIndex inside it (centered where possible, and
              pinned to the start/end once the deck doesn't leave room to
              center), so there's always exactly one active dot no matter how
              deep into the deck -- or how many "show me more" pages -- the
              reader is. */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-6)', padding: '8px 0 14px', borderTop: '1px solid var(--brand-sand)' }}>
            {(() => {
              const maxDots = 7;
              const total = books.length;
              const windowStart = total <= maxDots
                ? 0
                : Math.max(0, Math.min(currentIndex - Math.floor(maxDots / 2), total - maxDots));
              return books.slice(windowStart, windowStart + maxDots).map((_, offset) => {
                const i = windowStart + offset;
                return (
                  <button key={i} onClick={() => setCurrentIndex(i)}
                    style={{ width: i === currentIndex ? 16 : 6, height: 6, borderRadius: 3, background: i === currentIndex ? 'var(--brand-rust)' : 'var(--brand-sand)', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease', padding: 'var(--space-none)' }} />
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>

      {/* ── FULL DETAIL POPUP ── */}
      {detailBookId && (
        <BookDetailModal bookId={detailBookId} onClose={() => setDetailBookId(null)} onAuthRequired={onAuthRequired} />
      )}

      {/* ── REPORT MODAL ── */}
      {reportOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-body)' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={() => { setReportOpen(false); setReportReason(''); setReportSuccess(false); }} />
          <div style={{ position: 'relative', width: 340, background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', padding: '24px 22px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', zIndex: 1 }}>
            {reportSuccess ? (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ fontSize: 36, marginBottom: 'var(--space-10)' }}>✅</div>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Report submitted</p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Our team will review this book. Thanks for helping keep Reanlo accurate.</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-14)' }}>
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 3px' }}>Report this book</h3>
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{books[currentIndex]?.title}</p>
                  </div>
                  <button onClick={() => { setReportOpen(false); setReportReason(''); }} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-400)', cursor: 'pointer' }}>✕</button>
                </div>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 10px' }}>What's the issue?</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 'var(--space-lg)' }}>
                  {REPORT_REASONS.map(r => (
                    <button key={r} onClick={() => setReportReason(r)}
                      style={{ padding: '10px 14px', borderRadius: 'var(--radius-10)', border: reportReason === r ? '2px solid var(--brand-rust)' : '1.5px solid var(--brand-sand)', background: reportReason === r ? '#FBF7F0' : 'var(--brand-white)', color: reportReason === r ? 'var(--brand-rust)' : 'var(--neutral-600)', fontSize: 'var(--font-size-sm)', fontWeight: reportReason === r ? 700 : 400, cursor: 'pointer', textAlign: 'left' }}>
                      {r}
                    </button>
                  ))}
                </div>
                <button onClick={submitReport} disabled={!reportReason || reportSubmitting}
                  style={{ width: '100%', padding: 'var(--space-md)', background: reportReason && !reportSubmitting ? 'var(--brand-rust)' : 'var(--brand-sand)', color: reportReason && !reportSubmitting ? 'var(--brand-white)' : 'var(--neutral-400)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: reportReason && !reportSubmitting ? 'pointer' : 'not-allowed' }}>
                  {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
